import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../../redux/actions/growthEngine';
import { toast } from 'react-toastify';
import TiktokIcon from '../../../assets/images/admin/Tiktok.png';
import YoutubeIcon from '../../../assets/images/admin/Youtube.png';
import MetaIcon from '../../../assets/images/admin/meta.svg';
import { getGoalIconAndName, formatCampaignMoney, formatCampaignDateTime, getTimezoneLabel } from '../../../helper/utility';
import { getCampaignDisplayTitle, getCampaignObjectiveLabel } from '../../../helper/campaignDisplay';
import { getCampaignStatusBadge } from '../../../helper/campaignDisplayStatus';
import { CampaignThumbnail } from '../../../components/CampaignThumbnail';
import { ObjectiveIcon } from './ObjectiveIcon';
import MusicLoader from '../../../helper/MusicLoader';
import ChatInput from '../AmplitudeAI/Chat/ChatInput';
import { getArtistInfo, sendMessage } from '../../../redux/actions';
import { showToast } from '../../../helper/ShowToast';
import { PricingPlanModal } from '../../../components/PricingPlanModal';
import { CreditPlanModal } from '../../../components/CreditPlanModal';
import AppTour from '../../../components/AppTour/AppTour';
import { MetaConnectionBanner } from '../../../components/MetaConnectionBanner';
import { useMetaConnectionStatus } from '../../../queries/growthEngine/metaConnection.query';
import { useMetaCampaignHistoryList } from '../../../queries/growthEngine/metaCampaign.query';
import { getUserDisplayName } from '../../../helper/access';

const steps = [
  {
    target: "[data-tour='campaign-history']",
    content: "This is your Campaign History dashboard where you can view all your past and current campaigns. You can filter campaigns by platform, date range, and status to analyze their performance and gain insights for future campaigns.",
    placement: "bottom",
    title: "Campaign History"
  },
  {
    target: "[data-tour='amplitude-ai-chat']",
    content: "You can use Amplitude AI assistant for campaign insights and optimization. You can ask questions about your campaign performance, audience insights, and get recommendations to improve your campaign results.",
    placement: "top",
    title: "Amplitude AI Chat"
  },
];

export const CampaignHistory = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const {hash} = useLocation()
    const { showCampignList, goalOptions } = useSelector(state => state.growthEngine);
    const [runTour, setRunTour] = useState(false)
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const { showLoad } = useSelector((state) => state?.amplitudeReducer);
    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);
    const { artistInfo } = useSelector(state => state?.artistDetails)
    const access = useSelector((state) => state.auth.access);
    const authToken = useSelector((state) => state.auth.token);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const displayName = useMemo(
        () => getUserDisplayName(userDetails, artistInfo, access),
        [userDetails, artistInfo, access]
    );
    const [activeTab, setActiveTab] = useState('Campaigns');
    const tabs = ['Campaigns', 'Drafts'];
    const [limit] = useState(10);
    const [listParams, setListParams] = useState({
        page: 1,
        interval: '',
        effectiveStatus: '',
    });
    const [isRefreshing, setIsRefreshing] = useState(false);
    const { page, interval, effectiveStatus } = listParams;

    const metaConnectionUserKey =
        userDetails?.email || userDetails?.assignedArtistSongstateId || authToken || null;

    const {
        data: metaConnectionStatus,
        isLoading: isMetaConnectionLoading,
    } = useMetaConnectionStatus({
        userKey: metaConnectionUserKey,
        enabled: !!authToken && !!metaConnectionUserKey,
    });

    const {
        data: historyData,
        isLoading: isHistoryLoading,
        isFetching: isHistoryFetching,
        refetch: refetchHistory,
    } = useMetaCampaignHistoryList({
        userKey: metaConnectionUserKey,
        page,
        limit,
        interval,
        effectiveStatus,
        enabled: !!authToken && !!metaConnectionUserKey,
    });

    const total = historyData?.total ?? 0;
    const campaignList = historyData?.campaignList ?? [];
    const campaignLoading = (isHistoryLoading && !historyData) || isRefreshing;

    const handlePlatformChange = (platform) => {
        if (platform === "meta") {
            dispatch(actions.selectCampaignListPlatform(platform))
        }else{
            showToast('info','Coming soon...')
        }
    }

    useEffect(() => {
        if(hash === "#start-tour"){
            setRunTour(true)
        }
    }, [hash])

    useEffect(() => {
        if (hasActiveSubscription) {
            dispatch(getArtistInfo(toast))
        }
    }, [dispatch, hasActiveSubscription])

    const formatDateToLong = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    const handleIntervalChange = (nextInterval) => {
        setListParams((prev) => ({
            ...prev,
            page: 1,
            interval: nextInterval,
        }));
    }

    const handlePageChange = (newPage) => {
        setListParams((prev) => ({
            ...prev,
            page: newPage,
        }));
    };
    const handleStatusChange = (nextStatus) => {
        setListParams((prev) => ({
            ...prev,
            page: 1,
            effectiveStatus: nextStatus,
        }));
    }

    const handleRefresh = async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        try {
            await refetchHistory({ cancelRefetch: true });
        } finally {
            setIsRefreshing(false);
        }
    }

    const handleSend = (input) => {
        //BLOCK if no active subscription
        if (!hasActiveSubscription) {
            showToast("info", "Please subscribe to continue using Amplitude AI.");
            setShowPricingPlanModal(true);   // Open subscription modal
            return;
        }
        //BLOCK if no credits
        if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
            showToast("info", "You are out of credits. Please purchase more to continue.");
            setShowCreditPlanModal(true); // Open credit purchase modal
            return;
        }
        dispatch(sendMessage(toast, input));
        navigate("/admin/amplitude-ai");
    };

    return (
        <div className='CampaignHistory-page py-5'>
            <AppTour
                tourSteps={steps}
                runTour={runTour}
                setRunTour={setRunTour}
            />
            <div className="container max-w-full">
                <div className="card bg-gradient-to-r from-theme_clr1 to-theme_clr2 rounded-xl mb-5 p-4 lg:p-5">

                    <div className='flex flex-wrap items-center justify-between gap-[11px]'>
                        <h5 className='flex items-center text-white text-[23px] font-manrope font-bold leading-tight'>Campaign History <i className="bi bi-bar-chart-line-fill text-4xl leading-none ms-8"></i></h5>
                        <button className='btn-custom2 w-[192px] flex items-center justify-center gap-5 shadow-lg' onClick={() => navigate('/admin/growth-engine/campaign')}>New Campaign <i className='bi bi-plus-circle text-[20px]'></i></button>
                    </div>
                </div>

                <MetaConnectionBanner
                    metaConnectionStatus={metaConnectionStatus}
                    isLoading={isMetaConnectionLoading}
                    showSavedCampaignsNote
                />

                <div className="all-campaigns-list border-2 border-theme_clr2 rounded-lg mb-5 p-4" data-tour="campaign-history">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-4">
                        <select name="" id="" className="bg-transparent text-theme_clr1 text-base font-manrope font-medium border-0 focus:outline-none pl-3 py-2 pe-8"
                            value={interval}
                            onChange={(e) => handleIntervalChange(e.target.value)}
                        >
                            <option value="">All time statistics</option>
                            <option value="weekly">Weekly audience</option>
                            <option value="monthly">Monthly audience</option>
                            <option value="yearly">Yearly audience</option>
                        </select>

                        <div className="icons flex items-center gap-4">
                            {/* Meta */}
                            <div
                                onClick={() => handlePlatformChange("Meta")}
                                className={`cursor-pointer p-2 rounded-xl border transition-all duration-200 ${showCampignList === "Meta"
                                    ? "border-theme_clr2 shadow-md scale-105 bg-white/10"
                                    : "border-transparent"
                                    }`}
                            >
                                <img src={MetaIcon} alt="Meta" className="w-[28px]" />
                            </div>

                            {/* TikTok */}
                            <div
                                onClick={() => handlePlatformChange("TikTok")}
                                className={`cursor-pointer p-2 rounded-xl border transition-all duration-200 opacity-50 ${showCampignList === "TikTok"
                                    ? "border-theme_clr2 shadow-md scale-105 bg-white/10"
                                    : "border-transparent"
                                    }`}
                            >
                                <img src={TiktokIcon} alt="Tiktok" className="w-[30px]" />
                            </div>

                            {/* YouTube */}
                            <div
                                onClick={() => handlePlatformChange("YouTube")}
                                className={`cursor-pointer p-2 rounded-xl border transition-all duration-200 opacity-50 ${showCampignList === "YouTube"
                                    ? "border-theme_clr2 shadow-md scale-105 bg-white/10"
                                    : "border-transparent"
                                    }`}
                            >
                                <img src={YoutubeIcon} alt="Youtube" className="w-[30px]" />
                            </div>
                        </div>
                        <button className='bg-theme_clr2 rounded px-3 py-1 ml-auto'
                            onClick={() => handleRefresh()}
                            disabled={isRefreshing || isHistoryFetching}
                        >
                            <i className={`bi bi-arrow-clockwise text-white ${isRefreshing || isHistoryFetching ? 'animate-spin' : ''}`}></i>
                        </button>
                    </div>
                    <div className="content-wrapper">
                        {/* Tabs Menu Section */}
                        <div className="tabs-wrapper">
                            {/* Tabs Menu */}
                            <div className="flex border-b-2 border-slate-300">
                                {tabs.map((tab) => (
                                    <button key={tab} onClick={() => setActiveTab(tab)}
                                        className={`font-semibold border-b-2 border-slate-300 mb-[-2px] px-4 py-2 ${activeTab === tab ? 'border-theme_clr2 text-theme_clr2' : 'text-theme_clr1 hover:text-blue-500'}`}
                                    >
                                        {tab}
                                    </button>
                                ))}
                                <select name="" id="sortBy" className='bg-transparent text-theme_clr2 text-base font-manrope font-medium border-0 focus:outline-none ms-auto pl-3 py-2 pe-8'
                                    value={effectiveStatus}
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                >
                                    <option value="">Sort By</option>
                                    <option value="ACTIVE">Live</option>
                                    <option value="PENDING_REVIEW">In Review</option>
                                    <option value="PAUSED">Paused</option>
                                    <option value="ARCHIVED">Archived</option>
                                    <option value="CAMPAIGN_PAUSED">Finished</option>
                                </select>
                            </div>

                            {/* Tab Content */}
                            <div className="tab-content-wrapper lg:px-4 py-4">
                                {activeTab === 'Campaigns' && (
                                    <>
                                        {campaignLoading ? (
                                            <div className="flex justify-center items-center py-10">
                                                <MusicLoader />
                                            </div>
                                        ) : (
                                            <>
                                                {campaignList && campaignList.length > 0 ? (
                                                    campaignList.map((campaign, index) => {
                                                        const goalMatch = getGoalIconAndName(campaign.name, goalOptions);
                                                        const objectiveLabel = getCampaignObjectiveLabel(campaign, goalMatch);

                                                        const statusMap = {
                                                            PAUSED: {
                                                                label: 'Paused',
                                                                className: 'bg-gray-400 text-gray-800'
                                                            },
                                                            ACTIVE: {
                                                                label: 'Live',
                                                                className: 'bg-green-400 text-green-800'
                                                            },
                                                            IN_REVIEW: {
                                                                label: 'In Review',
                                                                className: 'bg-yellow-400 text-yellow-900'
                                                            },
                                                            ARCHIVED: {
                                                                label: 'Finished',
                                                                className: 'bg-red-600 text-white'
                                                            },
                                                            DELETED: {
                                                                label: 'Deleted',
                                                                className: 'bg-red-500 text-white'
                                                            },
                                                            PENDING_REVIEW: {
                                                                label: 'Pending Review',
                                                                className: 'bg-yellow-300 text-yellow-900'
                                                            },
                                                            DISAPPROVED: {
                                                                label: 'Disapproved',
                                                                className: 'bg-red-500 text-white'
                                                            },
                                                            PREAPPROVED: {
                                                                label: 'Preapproved',
                                                                className: 'bg-blue-300 text-blue-900'
                                                            },
                                                            IN_PROCESS: {
                                                                label: 'Processing',
                                                                className: 'bg-blue-400 text-white'
                                                            },
                                                            WITH_ISSUES: {
                                                                label: 'With Issues',
                                                                className: 'bg-orange-400 text-white'
                                                            },
                                                            CAMPAIGN_PAUSED: {
                                                                label: 'Campaign Paused',
                                                                className: 'bg-gray-400 text-gray-800'
                                                            },
                                                            ADSET_PAUSED: {
                                                                label: 'Adset Paused',
                                                                className: 'bg-gray-400 text-gray-800'
                                                            },
                                                            UNKNOWN: {
                                                                label: 'Unknown',
                                                                className: 'bg-slate-400 text-white'
                                                            }
                                                        };

                                                        const status = getCampaignStatusBadge(
                                                            campaign.effective_status,
                                                            campaign.endTime,
                                                            statusMap
                                                        );
                                                        return (
                                                            <div key={index} className="item-info flex flex-col sm:flex-row sm:flex-wrap gap-4 flex-1 bg-white border-b border-theme_clr4 pb-4 mb-4">
                                                                <CampaignThumbnail
                                                                    campaign={campaign}
                                                                    goalMatch={goalMatch}
                                                                    iconClassName="w-full h-full object-cover"
                                                                />
                                                                <div className="info-wrapper flex-1 flex flex-col lg:flex-row lg:flex-wrap lg:items-center justify-between gap-x-8 gap-y-2">
                                                                    <div className="content-wrapper">
                                                                        <div className="btn-wraaper flex items-center gap-4 mb-2">
                                                                            <button className='bg-theme_clr4 text-white text-[14px] font-inter font-medium flex items-center gap-1 tracking-[0.037] rounded-[4px] py-2 px-4'>
                                                                                {goalMatch ? (
                                                                                    <ObjectiveIcon
                                                                                        icon={goalMatch.icon}
                                                                                        name={goalMatch.name}
                                                                                        variant="white"
                                                                                        className="w-4 h-4"
                                                                                    />
                                                                                ) : null}
                                                                                {objectiveLabel}
                                                                            </button>
                                                                            {/* <button className='bg-theme_clr4 text-white text-[14px] font-inter font-medium flex items-center gap-1 tracking-[0.037] rounded-[4px] py-2 px-4'>
                                                        <img src={YoutubeIcon} alt="Youtube Icon" style={{ width: '16px' }} /> Grow your Video Views
                                                    </button>
                                                    <button className='bg-theme_clr4 text-white text-[14px] font-inter font-medium flex items-center gap-1 tracking-[0.037] rounded-[4px] py-2 px-4'>
                                                        <i className="bi bi-person-fill text-cyan-300"></i> Create a Presave
                                                    </button> */}
                                                                            <button
                                                                                className={`${status.className} text-[14px] min-w-[80px] font-inter font-medium flex items-center justify-center gap-1 text-center rounded-[4px] py-2 px-4`}
                                                                            >
                                                                                {status.label}
                                                                            </button>
                                                                            {/* <button className='bg-green-400 text-green-800 text-[14px] min-w-[80px] font-inter font-medium flex items-center justify-center gap-1 text-center rounded-[4px] py-2 px-4'>Live</button>
                                                                <button className='bg-yellow-400 text-yellow-900 text-[14px] min-w-[80px] font-inter font-medium flex items-center justify-center gap-1 text-center rounded-[4px] py-2 px-4'>In Review</button>
                                                                <button className='bg-red-400 text-theme_clr1 text-[14px] min-w-[80px] font-inter font-medium flex items-center justify-center gap-1 text-center rounded-[4px] py-2 px-4'>Finished</button> */}
                                                                        </div>
                                                                        <div className="text-theme_clr1 text-[20px] font-medium mb-1">
                                                                            {getCampaignDisplayTitle(campaign, artistInfo)}
                                                                        </div>
                                                                        <div className="text-theme_clr1 font-inter">
                                                                            <div className="flex flex-wrap items-center gap-2 mb-1"><span className='text-theme_clr4 font-bold'>Budget:</span><span>{formatCampaignMoney(campaign.totalBudget, campaign.currency)}</span>|{(campaign?.startTime && campaign?.endTime) ? <span>Running From {formatCampaignDateTime(campaign?.startTime, campaign?.timezone, { dateOnly: true })} to {formatCampaignDateTime(campaign?.endTime, campaign?.timezone, { dateOnly: true })}{campaign?.timezone ? ` · ${getTimezoneLabel(campaign.timezone)}` : ''}</span> : ''}</div>
                                                                            <div className="flex flex-wrap items-center gap-2"><span>Campaign ID: {campaign.metaCampaignId}</span><span className="h-[5px] w-[5px] bg-theme_clr1 inline-block rounded-full"></span> <span>Created on: {formatDateToLong(campaign.createdAt)} <i className="bi bi-box-arrow-up-right text-theme_clr4 ms-1"></i></span></div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="actions">
                                                                        <div className="flex items-center gap-8">
                                                                            <button className="text-theme_clr4 font-bold flex items-center gap-2"><i className="bi bi-copy"></i> Duplicate</button>
                                                                            <button
                                                                                className="text-theme_clr4 font-bold flex items-center gap-2 transition-transform duration-100 active:scale-95 active:shadow-inner"
                                                                                onClick={() => navigate(`/admin/growth-engine/campaign-editor/${campaign.metaCampaignId}`
                                                                                )}
                                                                            >
                                                                                View
                                                                                <i className="bi bi-chevron-right"></i></button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                    )) :
                                                    (
                                                        <div className="text-center text-lg font-bold text-theme_clr3 py-8">
                                                            No campaign found
                                                        </div>
                                                    )
                                                }

                                                <div className="flex items-center justify-center gap-2 mt-7">
                                                    {/* Previous Button */}
                                                    <button
                                                        disabled={page <= 1}
                                                        onClick={() => handlePageChange(page - 1)}
                                                        className={`text-white border rounded-md transition-all duration-200 px-4 py-2 
                                                             ${page <= 1 ? "bg-gray-400 cursor-not-allowed" : "bg-theme_clr3 hover:bg-theme_clr1"}`}
                                                    >
                                                        <i className="bi bi-chevron-double-left"></i>
                                                    </button>

                                                    {/* Page Indicator */}
                                                    <span className="bg-slate-100 text-theme_clr4 border text-base font-medium rounded-md transition-all duration-200 px-4 py-2">
                                                        Page {page} of {Math.ceil(total / limit)}
                                                    </span>

                                                    {/* Next Button */}
                                                    <button
                                                        disabled={page >= Math.ceil(total / limit)}
                                                        onClick={() => handlePageChange(page + 1)}
                                                        className={`text-white border rounded-md transition-all duration-200 px-4 py-2 
                                                           ${page >= Math.ceil(total / limit) ? "bg-gray-400 cursor-not-allowed" : "bg-theme_clr3 hover:bg-theme_clr1"}`}
                                                    >
                                                        <i className="bi bi-chevron-double-right"></i>
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                {activeTab === 'Drafts' && (
                                    <div className="text-theme_clr1 font-medium text-center text-lg">
                                        No drafts available.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
                <div data-tour="amplitude-ai-chat">
                    <ChatInput
                        title={`Launch campaigns to grow your fanbase, ${displayName}.`}
                        inputPlaceholder={'Use Amplitude in crafting the perfect campaign based on your vision.'}
                        isDisabled={showLoad}
                        onSend={handleSend}
                    />
                </div>
            </div>
            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/admin/growth-engine/campaign-history"} />
            <CreditPlanModal isOpen={showCreditPlanModal} onClose={() => setShowCreditPlanModal(false)} />
        </div>
    )
}
